import { Link } from 'react-router-dom';

export default function CodeExamples() {
  return (
    <div>
      <p>
        Below you can find Python and R code snippets showing how to programmatically interact with the REST
        API by means of YOUR_API_KEY.
        <br />
        <br />
        <b>⇒ Query the REST API with your API key</b>:  the code below shows how to retrieve gene-disease
        associations related to the gene <i>APP amyloid beta precursor protein</i> with NCBI ID equal to{' '}
        <a href="https://www.ncbi.nlm.nih.gov/gene/351" target="_blank" rel="noreferrer">
          351
        </a>{' '}
        (<i>gene_ncbi_id</i> parameter equal to 351). To this purpose we call the gene-disease associations
        endpoint (i.e. gda endpoint, see the REST API{' '}
        <Link to="#console" data-target="#console" data-toggle="tab" className="js-fix-scroll">
          Interactive console
        </Link>{' '}
        for more details). We retrieve the top-100 gene-disease associations involving such gene, ordered by
        descending gene-disease score, by asking for the first page of results (<i>page_number</i> parameter
        equal to 0).
      </p>
      {}
      <ul
        className="nav nav-tabs"
        style={{
          marginTop: '10px',
        }}
        id="codeExample"
        role="tablist"
      >
        <li className="nav-item">
          <a
            className="nav-link active"
            id="home-tab"
            data-toggle="tab"
            href="#python"
            role="tab"
            aria-controls="python"
            aria-selected="true"
          >
            Python
          </a>
        </li>
        <li className="nav-item">
          <a
            className="nav-link"
            id="profile-tab"
            data-toggle="tab"
            href="#rcode"
            role="tab"
            aria-controls="rcode"
            aria-selected="false"
          >
            R
          </a>
        </li>
      </ul>
      <div
        className="tab-content"
        style={{
          backgroundColor: 'white',
        }}
        id="codeExampleContent"
      >
        <div
          className="tab-pane fade show active"
          style={{
            padding: '10px',
          }}
          id="python"
          role="tabpanel"
          aria-labelledby="python-tab"
        >
          <pre>
            import requests{'\n'}
            import json{'\n'}
            import time{'\n'}
            {'\n'}
            <b># Provide your API key</b> {'\n'}API_KEY = "YOUR_API_KEY"{'\n'}
            {'\n'}
            <b># Specify query parameters by means of a dictionary</b> {'\n'}params = {'{'}
            {'}'}
            {'\n'}
            <b># ...retrieve disease associated to gene with NCBI ID equal to 351</b> {'\n'}
            params["gene_ncbi_id"] = "351"{'\n'}
            <b># ...retrieve the first page of results (page number 0)</b> {'\n'}
            params["page_number"] = "0"{'\n'}
            {'\n'}
            <b># Create a dictionary with the HTTP headers of your API call</b> {'\n'}
            HTTPheadersDict = {'{'}
            {'}'}
            {'\n'}
            <b># Set the 'Authorization' HTTP header equal to API_KEY (your API key)</b> {'\n'}
            HTTPheadersDict['Authorization'] = API_KEY{'\n'}
            <b>
              # Set the 'accept' HTTP header to specify the response format: one of 'application/json',
              'application/xml', 'application/csv'
            </b>{' '}
            {'\n'}HTTPheadersDict['accept'] = 'application/json'
            {'\n'}
            {'\n'}
            <b># Query the gda summary endpoint</b> {'\n'}response =
            requests.get("https://api.disgenet.com/api/v1/gda/summary",\
            {'\n'}
            {'                        '}params=params, headers=HTTPheadersDict, verify=False)
            {'\n'}
            {'\n'}
            <b>
              # If the status code of response is 429, it means you have reached one of your query limits{' '}
              {'\n'}# You can retrieve the time you need to wait until doing a new query in the response
              headers
            </b>{' '}
            {'\n'}if not response.ok:
            {'\n'}
            {' '.repeat(4)}if response.status_code == 429:
            {'\n'}
            {' '.repeat(8)}while response.ok is False:
            {'\n'}
            {' '.repeat(12)}print("You have reached a query limit for your user. Please wait {'{}'} seconds
            until next query".format(\
            {'\n'}
            {' '.repeat(12)}
            response.headers['x-rate-limit-retry-after-seconds']))
            {'\n'}
            {' '.repeat(12)}
            time.sleep(int(response.headers['x-rate-limit-retry-after-seconds']))
            {'\n'}
            {' '.repeat(12)}print("Your rate limit is now restored")
            {'\n'}
            {'\n'}
            {' '.repeat(12)}
            <b># Repeat your query</b>
            {'\n'}
            {' '.repeat(12)}response = requests.get("https://api.disgenet.com/api/v1/gda/summary",\
            {'\n'}
            {' '.repeat(36)}params=params, headers=HTTPheadersDict, verify=False)
            {'\n'}
            {' '.repeat(12)}if response.ok is True:
            {'\n'}
            {' '.repeat(16)}break
            {'\n'}
            {' '.repeat(12)}else:
            {'\n'}
            {' '.repeat(16)}continue
            {'\n'}
            {'\n'}
            <b>
              # Parse response content in JSON format since we set 'accept:application/json' as HTTP header
            </b>{' '}
            {'\n'}response_parsed = json.loads(response.text)
            {'\n'}print('STATUS: {'{'}
            {'}'}'.format(response_parsed["status"])){'\n'}
            print('TOTAL NUMBER OF RESULTS: {'{'}
            {'}'}
            '.format(response_parsed["paging"]["totalElements"]))
            {'\n'}print('NUMBER OF RESULTS RETRIEVED BY CURRENT CALL (PAGE NUMBER {'{'}
            {'}'}): {'{'}
            {'}'}'.format(\{'\n'}
            {'      '}
            response_parsed["paging"]["currentPageNumber"], response_parsed["paging"]["totalElementsInPage"]))
            {'\n'}
          </pre>
        </div>
        <div
          className="tab-pane fade"
          style={{
            padding: '10px',
          }}
          id="rcode"
          role="tabpanel"
          aria-labelledby="rcode-tab"
        >
          <pre>
            library(httr){'\n'}library(jsonlite){'\n'}
            {'\n'}
            <b># Provide your API key</b> {'\n'}api_key &lt;- "YOUR_API_KEY"{'\n'}
            {'\n'}
            <b>
              # Specify the response format: one of 'application/json', 'application/xml', 'application/csv'
            </b>{' '}
            {'\n'}resp_format &lt;- "application/json"{'\n'}
            {'\n'}
            <b># Query the gda endpoint by specifying the following parameters:</b>
            {'\n'}
            <b># - gene_ncbi_id=351: retrieve disease associated to gene with NCBI ID equal to 351</b>
            {'\n'}
            <b># - page_number=0: retrieve the first page of results (page number 0)</b>
            {'\n'}
            <b>
              # and providing your API key (api_key) and the response format (resp_format) as HTTP headers
            </b>
            {'\n'}res &lt;- GET("https://api.disgenet.com/api/v1/gda/summary?gene_ncbi_id=351&page_number=0",
            {'\n'}
            {'           '}add_headers(.headers = c('Authorization'= api_key,{'\n'}
            {'                                    '}'accept' = resp_format))){'\n'}
            {'\n'}
            <b># If the status code of response is 429, it means you have reached one of your query limits</b>
            {'\n'}
            <b>
              # You can retrieve the time you need to wait until doing a new query in the response headers
            </b>
            {'\n'}
            if(res$status_code == 429) &#123;
            {'\n'}
            {'    '}timetoWait &lt;- res$headers$`x-rate-limit-retry-after-seconds`
            {'\n'}
            {'    '}print(paste0("You have reached a query limit for your user. Please wait ", timetoWait, "
            seconds until next query"))
            {'\n'}
            {'    '}Sys.sleep(timetoWait)
            {'\n'}
            {'    '}print("Your rate limit is now restored.")
            <b>
              {'\n'}
              {'    '}# Repeat your query
            </b>
            {'\n'}
            {'    '}res &lt;- GET("http://api.disgenet.com/api/v1/gda/summary?gene_ncbi_id=351&page_number=0",
            {'\n'}
            {'           '}add_headers(.headers = c('Authorization'= api_key,{'\n'}
            {'                                    '}'accept' = resp_format)))
            {'\n'}
            &#125;
            {'\n'}
            {'\n'}
            <b>
              # Extract the content type of the response and parse the JSON content since we set
              'accept:application/json' as HTTP header
            </b>{' '}
            {'\n'}http_type(res) <b># "application/json"{'\n'}</b>
            {'\n'}query_result &lt;- fromJSON(rawToChar(res$content))
            {'\n'}query_result
            {'\n'}
            {'    '}
            {'\t'}
            {'\t'}
            {'  '}
          </pre>
        </div>
      </div>
    </div>
  );
}
